import React from 'react';
import { Icon } from '@economic/taco';
import { t } from '@lingui/macro';
import PackageCardFuctionality from './PackageCardFunctionality';

interface Props {
    planId: number;
    functionalityHeader: string;
    functionalities: { text: string; header?: boolean }[];
}
interface Functionality {
    id: string;
    name: string;
}

export default function PackageCardFunctionalities({ planId, functionalityHeader, functionalities }: Props) {
    // Basis, Plus, Smart and Advanced plans are from 111 to 130. We only want to show this list of features for these plans.
    if (planId >= 111 && planId <= 130) {
        const isAvailable = (functionalityId: string) => {
            switch (functionalityId) {
                case 'core':
                case 'bank':
                    return true; // always available
                case 'accessToIntegrations':
                case 'ordersQuotesReminders':
                    return planId > 115; // is at least plus plan
                case 'autosuggest':
                case 'autoAttachments':
                    return planId >= 121; // is smart of advanced plan
                case 'attachmentApproval':
                case 'dimensions':
                case 'holdingCompany':
                case 'extraUser':
                    return planId >= 126; // is advanced plan
                default:
                    return false;
            }
        };

        const functionalitiesForBasicPlusAdvancedSmartPlans: Functionality[] = [
            { id: 'core', name: t`Core features` },
            { id: 'bank', name: t`Bank integration` },
            { id: 'accessToIntegrations', name: t`Free access to integrations` },
            { id: 'ordersQuotesReminders', name: t`Orders, quotes and reminders` },
            { id: 'autosuggest', name: t`Auto-suggest in bank reconciliation` },
            { id: 'autoAttachments', name: t`Automatic document management` },
            { id: 'attachmentApproval', name: t`Multiple Approval` },
            { id: 'dimensions', name: t`Multiple dimensions` },
            { id: 'holdingCompany', name: t`Subscription for holding company` },
            { id: 'extraUser', name: t`Extra user` },
        ];

        return (
            <div className="p-4 text-xs">
                {functionalitiesForBasicPlusAdvancedSmartPlans.map((functionality, index) => (
                    <PackageCardFuctionality
                        functionality={functionality}
                        index={index}
                        isAvailable={isAvailable(functionality.id)}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className="pl-4">
            <div className="mb-3 h-6">{functionalityHeader}</div>
            <div className="text-xs h-72">
                {functionalities.map(functionality => (
                    <div className="flex leading-4 mb-3 items-center" key={functionality.text}>
                        {functionality.header ? (
                            <span className="ml-2 pt-2 pb-1">{functionality.text}</span>
                        ) : (
                            <>
                                <Icon name="tick" color="green" />
                                {functionality.text}
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
